// Bag totals breakdown for the Fashion till. Pure: it only sorts the figures
// the cart already holds into rows. It never prices or taxes anything again.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

export const isActiveLine = (line) =>
  !!line && line.isCanceled !== "Y" && num(line.weight) !== 0;

// Rule ids that count as a bill (total-level) discount on this cart.
export const billRuleIds = (cart) => {
  const ids = new Set();
  const total = cart?.manualDiscountData?.totalLevelDiscount;
  if (Array.isArray(total)) {
    total.forEach((d) => {
      if (d?.pricingRule) ids.add(String(d.pricingRule));
    });
  }
  if (typeof cart?.totalBillDicount === "string" && cart.totalBillDicount) {
    ids.add(cart.totalBillDicount);
  }
  return ids;
};

// Rule ids of manual line discounts (FD / PD) the cashier gave on this cart.
export const lineRuleIds = (cart) => {
  const ids = new Set();
  const lines = cart?.manualDiscountData?.lineLevelDiscount;
  if (Array.isArray(lines)) {
    lines.forEach((d) => {
      if (d?.pricingRule) ids.add(String(d.pricingRule));
    });
  }
  return ids;
};

// The part of one line's discount that came from a bill rule.
export const lineBillDiscount = (line, ids) => {
  if (!ids.size) return 0;
  const lineDisc = num(line.discount);
  const all = Array.isArray(line.allDiscounts) ? line.allDiscounts : [];
  const billEntries = all.filter((d) => d && ids.has(String(d.mPricingruleId)));
  if (billEntries.length && billEntries.every((d) => d.appliedAmount !== undefined && d.appliedAmount !== null)) {
    return billEntries.reduce((s, d) => s + num(d.appliedAmount), 0);
  }
  // A bill rule with no recorded split: the whole line discount is the bill's.
  if (billEntries.length) return lineDisc;
  if (!all.length && line.mPricingruleId && ids.has(String(line.mPricingruleId))) return lineDisc;
  return 0;
};

const lineTax = (line) => num(line.taxAmount);
const lineRate = (line) => num(line?.tax?.rate ?? line?.taxRate);

// GST grouped by rate. Prefers the grouping the cart already carries.
export const gstByRate = (cart, lines) => {
  const grouped = cart?.groupedItemsByTaxRate;
  const map = new Map();
  if (Array.isArray(grouped) && grouped.length) {
    grouped.forEach((g) => {
      const rate = num(g?.taxRate);
      map.set(rate, (map.get(rate) || 0) + num(g?.totalLinetax));
    });
  } else {
    lines.forEach((l) => {
      const rate = lineRate(l);
      map.set(rate, (map.get(rate) || 0) + lineTax(l));
    });
  }
  return [...map.entries()]
    .filter(([, amount]) => round2(amount) !== 0)
    .sort((a, b) => a[0] - b[0])
    .map(([rate, amount]) => ({ rate, amount: round2(amount) }));
};

/**
 * Rows for the bag, in display order:
 * items, offer discount, line discounts, bill discount, taxable value, GST by rate, round-off, to pay.
 */
export function billTotals(cart) {
  const lines = (Array.isArray(cart?.items) ? cart.items : []).filter(isActiveLine);
  const ids = billRuleIds(cart);
  const lineIds = lineRuleIds(cart);
  ids.forEach((id) => lineIds.delete(id));
  let net = 0;
  let discount = 0;
  let bill = 0;
  let manual = 0;
  let tax = 0;
  let units = 0;
  lines.forEach((l) => {
    net += num(l.nettotal);
    discount += num(l.discount);
    bill += lineBillDiscount(l, ids);
    manual += lineBillDiscount(l, lineIds);
    tax += lineTax(l);
    units += Math.abs(num(l.weight));
  });
  bill = Math.min(Math.max(bill, 0), Math.max(discount, 0));
  manual = Math.min(Math.max(manual, 0), Math.max(discount - bill, 0));
  const roundOff = num(cart?.roundOff);
  const total = cart?.total;
  const toPay = total !== undefined && total !== null && total !== "" && Number.isFinite(Number(total))
    ? Number(total)
    : round2(net + roundOff);
  return {
    units,
    items: round2(net + discount),
    offerDiscount: round2(discount - bill - manual),
    lineDiscount: round2(manual),
    billDiscount: round2(bill),
    taxable: round2(net - tax),
    gst: gstByRate(cart, lines),
    roundOff: round2(roundOff),
    toPay: round2(toPay),
  };
}

// Prices on the shelf already carry GST unless the store says otherwise.
export const taxIncluded = (tillData) => (tillData?.tillAccess?.csBunit?.isTaxIncluded || "Y") === "Y";

// Intra-state GST is half central, half state; the state half takes the odd paisa.
export const gstSplit = (amount) => {
  const cgst = round2(num(amount) / 2);
  return { cgst, sgst: round2(num(amount) - cgst) };
};

// What the customer saved on this bill: offer, line and bill discounts together.
export const billSavings = (totals) => round2(num(totals?.offerDiscount) + num(totals?.lineDiscount) + num(totals?.billDiscount));

export const gstLabel = (rate, included) => `GST ${rate}%${included ? " included" : ""}`;

// One line for tax-inclusive bills: "GST 5% + 12% included". Excluded GST keeps a row per rate,
// since those amounts add to the total.
export const gstSummaryLabel = (rates, included) => `GST ${rates.map((r) => `${r}%`).join(" + ")}${included ? " included" : ""}`;
